import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ApplicationSourceChip from 'src/components/ApplicationSourceChip';
import Iconify from 'src/components/Iconify';
import NoteTooltip from 'src/components/NoteTooltip';
import StatusChip from 'src/components/StatusChip';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { useWin32FileSetDetail } from 'src/hooks/wdac';
import { formatDate } from 'src/utils/formatTime';
import { getCatalogGenerationStatusLabel } from 'src/utils/enums/catalogGenerationStatus';
import {
  CUSTOMER_ENVIRONMENT_APPLICATION_SOURCE,
  getCustomerEnvironmentApplicationSourceLabel,
} from 'src/utils/enums/customerEnvironmentApplicationSource';
import {
  getCustomerEnvironmentApplicationStatusColor,
  getCustomerEnvironmentApplicationStatusLabel,
} from 'src/utils/enums/customerEnvironmentApplicationStatus';
import FileSetSectionTable from 'src/features/detailSections/FileSetSectionTable';
import {
  FILES_SECTION,
  OUTSIDE_PATH_RULES_SECTION,
  PUBLISHERS_SECTION,
  WRITABLE_FOLDERS_SECTION,
} from 'src/features/detailSections/sections';
import { INSTALLATION_FIELDS } from 'src/features/detailSections/summaryFields';
import FileSetTemplateLinks from './FileSetTemplateLinks';

// The pair below the publishers card matches on whichever has more rows, so these only bound how
// far that can go in either direction.
const SIDE_BY_SIDE_MIN_HEIGHT = 110;
const SIDE_BY_SIDE_MAX_HEIGHT = 260;

const Win32FileSetModal = ({ open, onClose, globalApplicationVersionId, fileSetId }) => {
  const { data: detail, isLoading } = useWin32FileSetDetail({
    globalApplicationVersionId,
    fileSetId,
    enabled: open,
  });

  // Each card reads its own paged endpoint, so they only differ by the section they are given.
  const sectionTable = (section, props) => (
    <FileSetSectionTable
      key={`${fileSetId}-${section.key}`}
      globalApplicationVersionId={globalApplicationVersionId}
      fileSetId={fileSetId}
      section={section}
      enabled={open}
      {...props}
    />
  );

  const statusColor = getCustomerEnvironmentApplicationStatusColor(detail?.status);
  const showInstallationDetails =
    detail?.originalSource === CUSTOMER_ENVIRONMENT_APPLICATION_SOURCE.InstallationImport;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <span>File Set Import</span>
          {detail?.originalSource ? (
            <ApplicationSourceChip
              source={detail.originalSource}
              sx={{ height: 20, fontSize: '10.5px' }}
            />
          ) : null}
          {detail?.status ? (
            <StatusChip
              label={getCustomerEnvironmentApplicationStatusLabel(detail.status)}
              color={statusColor}
              backgroundColor={`${statusColor}1F`}
              sx={{ height: 20, fontSize: '0.68rem' }}
            />
          ) : null}
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {isLoading || !detail ? (
            <Skeleton variant="rounded" height={140} sx={{ bgcolor: '#484848' }} />
          ) : (
            <>
              <DetailCard compact>
                <Stack spacing={2}>
                  <SummaryValue label="File Set Hash" compact>
                    {detail.fileSetHash}
                  </SummaryValue>
                  <SummaryGrid compact>
                    <SummaryValue label="Files" compact>
                      {(detail.fileCount ?? 0).toLocaleString()}
                    </SummaryValue>
                    <SummaryValue label="WDAC Impacted Devices" compact>
                      {(detail.impactedDevicesCount ?? 0).toLocaleString()}
                    </SummaryValue>
                    <SummaryValue label="Identified" compact>
                      {detail.identifiedOn ? formatDate(detail.identifiedOn) : null}
                    </SummaryValue>
                    <SummaryValue label="Source" compact>
                      {getCustomerEnvironmentApplicationSourceLabel(detail.originalSource) ||
                        'Telemetry only'}
                    </SummaryValue>
                    <SummaryValue
                      label="Catalog Generation"
                      compact
                      valueAdornment={<NoteTooltip note={detail.notes} />}>
                      {detail.catalogGenerationStatus
                        ? getCatalogGenerationStatusLabel(detail.catalogGenerationStatus)
                        : null}
                    </SummaryValue>
                    <SummaryValue label="Templates" compact>
                      <FileSetTemplateLinks
                        fileSet={{
                          applicationId: detail.applicationId,
                          templates: detail.templates,
                        }}
                        emptyLabel="None"
                      />
                    </SummaryValue>
                  </SummaryGrid>
                </Stack>
              </DetailCard>

              {showInstallationDetails ? (
                <DetailCard compact>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">Installation Details</Typography>
                    <SummaryGrid compact>
                      {INSTALLATION_FIELDS.map(field => (
                        <SummaryValue key={field.label} label={field.label} compact>
                          {field.read(detail)}
                        </SummaryValue>
                      ))}
                    </SummaryGrid>
                  </Stack>
                </DetailCard>
              ) : null}
            </>
          )}

          {sectionTable(FILES_SECTION, { maxHeight: 340 })}
          {sectionTable(PUBLISHERS_SECTION, { maxHeight: 220 })}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            {[OUTSIDE_PATH_RULES_SECTION, WRITABLE_FOLDERS_SECTION].map(section => (
              <Box key={section.key} sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
                {sectionTable(section, {
                  fill: true,
                  minHeight: SIDE_BY_SIDE_MIN_HEIGHT,
                  maxHeight: SIDE_BY_SIDE_MAX_HEIGHT,
                })}
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default Win32FileSetModal;
